import { BACKEND_URL } from "@/lib/backendUrl";
import { useEffect, useState } from "react";
import { FileUp, Loader2, Paperclip, Plus, Trash2, TriangleAlert, UserCog } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const REASSIGN_ROLES = ["board_chair", "platform_admin", "org_admin"];

const Row = ({ label, children }) => (
  <div className="min-w-0">
    <Label className="mb-1 block text-[10.5px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const WorkMoney = ({ item, canWrite, onChanged }) => {
  const { user } = useAuth();
  const detail = item.event_detail || {};
  const [funds, setFunds] = useState([]);
  const [busy, setBusy] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [newItem, setNewItem] = useState({ item: "", qty: 1, available: 0, note: "" });
  const [expense, setExpense] = useState({ amount: "", description: "", incurred_on: "",
    fund_id: "", task_index: "", purchase_item: "", payee: "" });
  const [owner, setOwner] = useState("");

  useEffect(() => {
    api.get("/charity/funds").then((r) => setFunds(r.data)).catch(() => setFunds([]));
  }, []);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      const { data } = await fn();
      toast.success(message);
      onChanged(data);
      return data;
    } catch (e) {
      const dup = e?.response?.data?.detail?.duplicate_warnings;
      if (dup) {
        setWarnings(dup);
        toast.warning("This may already have been claimed — check below");
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setBusy("");
    }
    return null;
  };

  const addItem = () => run("item",
    () => api.post(`/works/${item.id}/items`, { ...newItem, qty: Number(newItem.qty) || 1,
                                                available: Number(newItem.available) || 0 }),
    `${newItem.item} added to the list`).then((d) => {
      if (d) setNewItem({ item: "", qty: 1, available: 0, note: "" });
    });

  const removeItem = (index) => run(`item-${index}`,
    () => api.delete(`/works/${item.id}/items/${index}`), "Item removed");

  const addExpense = (confirmDuplicate = false) => run("expense",
    () => api.post(`/works/${item.id}/expenses`, {
      ...expense, amount: Number(expense.amount) || 0,
      task_index: expense.task_index === "" ? null : Number(expense.task_index),
      confirm_duplicate: confirmDuplicate,
    }), "Expense posted to the ledger").then((d) => {
      if (d) {
        setExpense({ amount: "", description: "", incurred_on: "", fund_id: "", task_index: "",
                     purchase_item: "", payee: "" });
        setWarnings([]);
      }
    });

  const attachBill = async (ref, file) => {
    if (!file) return;
    setBusy(ref);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/works/${item.id}/expenses/${ref}/bill`, body,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bill attached");
      onChanged(data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const reassign = () => run("owner",
    () => api.post(`/works/${item.id}/reassign`, { owner, note: "" }),
    `Handed to ${owner}`).then((d) => { if (d) setOwner(""); });

  const expenses = detail.expenses || [];
  const items = detail.item_list || [];
  const tasks = detail.task_list || [];
  const spent = expenses.reduce((a, e) => a + (e.amount || 0), 0);
  const mayReassign = REASSIGN_ROLES.includes(user?.role);

  return (
    <div className="space-y-4" data-testid="work-money">
      <Panel testId="work-where-when" kicker="Where and when" title="Place, dates and scale">
        <div className="grid grid-cols-2 gap-3 p-3 text-[11.5px] sm:grid-cols-3">
          {[["Place", detail.place], ["Starts", shortDate(detail.starts_on)],
            ["Ends", shortDate(detail.ends_on)], ["Capacity", detail.capacity],
            ["Ticket price", detail.ticket_price ? money(detail.ticket_price, "INR") : ""],
            ["Goal", detail.goal ? money(detail.goal, "INR", true) : ""]].map(([label, value]) => (
            <div key={label}>
              <div className="text-[10px] uppercase tracking-[1px] text-muted-foreground">{label}</div>
              <div className="text-foreground/85" data-testid={`work-detail-${label.toLowerCase().replace(" ", "-")}`}>
                {value || "—"}
              </div>
            </div>
          ))}
        </div>
        {mayReassign && canWrite && (
          <div className="flex flex-wrap items-end gap-2 border-t border-border p-3">
            <Row label="Hand this work to">
              <Input value={owner} onChange={(e) => setOwner(e.target.value)}
                     placeholder="their email" data-testid="work-reassign-input"
                     className="h-8 w-[220px] bg-muted/40 text-[12px]" />
            </Row>
            <Button size="sm" variant="outline" onClick={reassign} disabled={!owner || busy === "owner"}
                    data-testid="work-reassign-button" className="h-8 rounded-full text-[11px]">
              {busy === "owner" ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                : <UserCog className="mr-1.5 h-3 w-3" />} Reassign
            </Button>
            <span className="text-[10.5px] text-muted-foreground">
              Currently with {item.owner || "nobody"}
            </span>
          </div>
        )}
      </Panel>

      <Panel testId="work-items-required" kicker="Logistics" title="Items required">
        <div className="divide-y divide-border">
          {items.length === 0 && (
            <div className="px-3 py-4 text-[11.5px] text-muted-foreground">Nothing listed yet.</div>
          )}
          {items.map((it, index) => (
            <div key={`${it.item}-${index}`}
                 className="flex items-center justify-between px-3 py-2 text-[11.5px]"
                 data-testid={`work-item-row-${index}`}>
              <div className="min-w-0">
                <span className="text-foreground/85">{it.item}</span>
                {it.note && <span className="ml-2 text-[10.5px] text-muted-foreground">{it.note}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-muted-foreground">
                  {it.available}/{it.qty}
                  {it.available < it.qty && (
                    <span className="ml-2 text-amber-600 dark:text-amber-400">short</span>
                  )}
                </span>
                {canWrite && (
                  <Button size="sm" variant="ghost" className="h-6 px-1.5"
                          data-testid={`work-item-remove-${index}`}
                          disabled={busy === `item-${index}`} onClick={() => removeItem(index)}>
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        {canWrite && (
          <div className="grid grid-cols-2 items-end gap-2 border-t border-border p-3 sm:grid-cols-5">
            <div className="sm:col-span-2">
              <Row label="Item needed">
                <Input value={newItem.item} onChange={(e) => setNewItem({ ...newItem, item: e.target.value })}
                       data-testid="work-item-name-input" className="h-8 bg-muted/40 text-[12px]" />
              </Row>
            </div>
            <Row label="Needed">
              <Input type="number" value={newItem.qty}
                     onChange={(e) => setNewItem({ ...newItem, qty: e.target.value })}
                     data-testid="work-item-qty-input" className="h-8 bg-muted/40 font-mono text-[12px]" />
            </Row>
            <Row label="In hand">
              <Input type="number" value={newItem.available}
                     onChange={(e) => setNewItem({ ...newItem, available: e.target.value })}
                     data-testid="work-item-available-input"
                     className="h-8 bg-muted/40 font-mono text-[12px]" />
            </Row>
            <Button size="sm" onClick={addItem} disabled={!newItem.item || busy === "item"}
                    data-testid="work-item-add-button"
                    className="h-8 rounded-full bg-primary text-[11px] text-primary-foreground">
              {busy === "item" ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                : <Plus className="mr-1.5 h-3 w-3" />} Add
            </Button>
          </div>
        )}
      </Panel>

      <Panel testId="work-expenses" kicker="Money spent" title="Expenses and bills">
        <div className="divide-y divide-border">
          {expenses.length === 0 && (
            <div className="px-3 py-4 text-[11.5px] text-muted-foreground">
              Nothing spent on this yet.
            </div>
          )}
          {expenses.map((e) => (
            <div key={e.ref} className="flex items-center justify-between gap-3 px-3 py-2 text-[11.5px]"
                 data-testid={`work-expense-row-${e.ref}`}>
              <div className="min-w-0">
                <div className="text-foreground/85">{e.description || e.task_name || e.ref}</div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  {shortDate(e.incurred_on)}
                  {e.task_name && ` · ${e.task_name}`}
                  {e.purchase_item && ` · ${e.purchase_item}`}
                  {e.source === "expense_claim" && " · from an expense claim"}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="font-mono">{money(e.amount, "INR")}</span>
                {e.bill ? (
                  <a href={`${BACKEND_URL}/api/works/${item.id}/expenses/${e.ref}/bill`}
                     target="_blank" rel="noreferrer" data-testid={`work-expense-bill-${e.ref}`}
                     className="inline-flex items-center text-[10.5px] text-primary hover:underline">
                    <Paperclip className="mr-1 h-3 w-3" /> bill
                  </a>
                ) : canWrite && (
                  <label className="inline-flex cursor-pointer items-center rounded-full border border-border px-2 py-0.5 text-[10.5px] hover:border-primary/60">
                    <FileUp className="mr-1 h-3 w-3" /> bill
                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp"
                           data-testid={`work-expense-bill-input-${e.ref}`}
                           onChange={(ev) => attachBill(e.ref, ev.target.files?.[0])} />
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border px-3 py-2 text-[11.5px]">
          <span className="text-muted-foreground">Posted to the ledger</span>
          <span className="font-mono text-foreground/85" data-testid="work-expense-total">
            {money(spent, "INR")}
            {detail.budget?.planned ? ` of ${money(detail.budget.planned, "INR", true)} planned` : ""}
          </span>
        </div>

        {warnings.length > 0 && (
          <div className="m-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3"
               data-testid="work-expense-duplicate-warning">
            <div className="mb-1 flex items-center gap-2 text-[11.5px] font-semibold text-amber-700 dark:text-amber-300">
              <TriangleAlert className="h-3.5 w-3.5" /> This may already have been claimed
            </div>
            {warnings.map((w) => (
              <p key={w} className="text-[11px] text-amber-700/90 dark:text-amber-200/90">{w}</p>
            ))}
          </div>
        )}

        {canWrite && (
          <div className="grid grid-cols-2 items-end gap-2 border-t border-border p-3 sm:grid-cols-4">
            <Row label="Amount">
              <Input type="number" value={expense.amount}
                     onChange={(ev) => setExpense({ ...expense, amount: ev.target.value })}
                     data-testid="work-expense-amount-input"
                     className="h-8 bg-muted/40 font-mono text-[12px]" />
            </Row>
            <Row label="Date">
              <Input type="date" value={expense.incurred_on}
                     onChange={(ev) => setExpense({ ...expense, incurred_on: ev.target.value })}
                     data-testid="work-expense-date-input"
                     className="h-8 bg-muted/40 font-mono text-[12px]" />
            </Row>
            <Row label="Paid from">
              <Select value={expense.fund_id}
                      onValueChange={(v) => setExpense({ ...expense, fund_id: v })}>
                <SelectTrigger data-testid="work-expense-fund-select" className="h-8 bg-muted/40 text-[12px]">
                  <SelectValue placeholder="Fund" /></SelectTrigger>
                <SelectContent>
                  {funds.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.code} · {money(f.balance, f.currency, true)}
                    </SelectItem>))}
                </SelectContent>
              </Select>
            </Row>
            <Row label="Against which task">
              <Select value={expense.task_index === "" ? "none" : String(expense.task_index)}
                      onValueChange={(v) => setExpense({ ...expense,
                                                         task_index: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="work-expense-task-select" className="h-8 bg-muted/40 text-[12px]">
                  <SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No particular task</SelectItem>
                  {tasks.map((t, i) => (
                    <SelectItem key={`${t.name}-${i}`} value={String(i)}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </Row>
            <div className="sm:col-span-2">
              <Row label="What was bought">
                <Input value={expense.description}
                       onChange={(ev) => setExpense({ ...expense, description: ev.target.value })}
                       data-testid="work-expense-description-input"
                       className="h-8 bg-muted/40 text-[12px]" />
              </Row>
            </div>
            <Row label="Paid to">
              <Input value={expense.payee}
                     onChange={(ev) => setExpense({ ...expense, payee: ev.target.value })}
                     data-testid="work-expense-payee-input" className="h-8 bg-muted/40 text-[12px]" />
            </Row>
            <Button size="sm" onClick={() => addExpense(warnings.length > 0)}
                    disabled={!expense.amount || !expense.fund_id || busy === "expense"}
                    data-testid="work-expense-add-button"
                    className="h-8 rounded-full bg-primary text-[11px] text-primary-foreground">
              {busy === "expense" ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                : <Plus className="mr-1.5 h-3 w-3" />}
              {warnings.length > 0 ? "Post it anyway" : "Post expense"}
            </Button>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default WorkMoney;
